/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { Suspense } from 'react'
import Image from 'next/legacy/image'
import { Layout } from 'components/materia/layout'
import { Tabs, Text } from '@hashicorp/mds-react/components'
import { TabsHandler } from 'components/materia/tabs/components/tabs-handler'
import s from './timeline-tabs.module.css'

interface TimelineEvent {
	date: string
	heading: string
	icon?: {
		src: string
		alt?: string
	}
	link?: {
		label: string
		url: string
	}
}

interface TimelineData {
	data: Array<TimelineEvent>
}

function Timeline({ data }: TimelineData): JSX.Element {
	return (
		<ol className={s.timeline} id="timeline">
			{data.map((event) => (
				<Event key={event.heading} {...event} />
			))}
		</ol>
	)
}

const TimelinePanel = ({ events }: { events: TimelineEvent[] }) => {
	return (
		<Tabs.Panel>
			<Timeline data={events} />
		</Tabs.Panel>
	)
}

function Event({ date, heading, icon, link }: TimelineEvent): JSX.Element {
	return (
		<li className={s.event}>
			<div className={s.eventContainer}>
				<time>
					<Text.Body color="primary" weight="bold" size="400">
						{date}
					</Text.Body>
				</time>
				<div className={s.eventContent}>
					{icon && icon.src && (
						<div className={s.eventContentMedia}>
							<Image src={icon.src} width={32} height={32} alt={icon.alt} />
						</div>
					)}
					<div>
						<Text.Body color="faint">{heading}</Text.Body>
						{link && (
							<a href={link.url} className={s.eventContentLink}>
								{link.label}
							</a>
						)}
					</div>
				</div>
			</div>
		</li>
	)
}

export const TimelineTabs = ({ panels }) => {
	const panelNames = panels.map((panel) => {
		return {
			name: panel.name,
		}
	})
	return (
		<Layout
			className={s.root}
			layout={{
				paddingTop: 'none',
				paddingBottom: 'small',
				appearance: 'dark',
				background: 'primary',
			}}
		>
			<Suspense>
				<TabsHandler panels={panelNames} updateUrlOnChange={true}>
					{panels.map((panel, id) => {
						return <TimelinePanel key={panel.name} events={panel.events} />
					})}
				</TabsHandler>
			</Suspense>
		</Layout>
	)
}
